import { isBrokerWebAuthorization } from './brokerWebAuthorization'
import { JSBridge } from '@/utils/jsBridge'
import { getCookie, getDeviceInfo as getCookieDeviceInfo, getLanguage as getCookieLanguage, UserInfoKey } from '@/utils/cookies'

const WEBSITE_IDS = {
  production: '39fd04cf-4d79-4e7d-ba6b-b6da5e763c05',
  test: 'c90049b9-f78a-41bb-b70a-485db1179de2',
  development: 'c90049b9-f78a-41bb-b70a-485db1179de2'
}

const DEFAULT_DOMAINS = {
  production: 'm.mobiussocial.com',
  test: 'm.messeai-test.com',
  development: 'm.messeai-test.com'
}

// 统一将输入转成可用字符串（去空白/非字符串兜底）
const sanitizeString = (value) =>
  typeof value === 'string'
    ? value.trim()
    : value === undefined || value === null
      ? ''
      : String(value).trim()

const CACHE_TTL_MS = 60 * 1000
// Umami 脚本地址由环境变量注入，便于按环境切换
const UMAMI_SCRIPT_URL = sanitizeString(import.meta.env.VITE_UMAMI_SCRIPT_URL || '')

// 判断是否为纯对象（排除数组）
const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value)

// 获取运行环境标识（小写化）
const getEnv = () => sanitizeString(import.meta.env.VITE_ENV || import.meta.env.MODE || '').toLowerCase()

// 根据环境选择 Umami 站点 ID
const pickWebsiteId = () => {
  const env = getEnv()
  return WEBSITE_IDS[env] || WEBSITE_IDS.test
}

// 根据环境选择固定域名（不依赖当前访问域名）
const pickHostname = () => {
  const env = getEnv()
  // hostname 按环境固定，不依赖当前域名
  return DEFAULT_DOMAINS[env] || DEFAULT_DOMAINS.test
}

// 通过 UA 推断平台，仅作兜底
const parsePlatformFromUA = () => {
  if (typeof navigator === 'undefined') {
    return ''
  }
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod/.test(ua)) return 'ios'
  if (/android/.test(ua)) return 'android'
  return ''
}

const hasAppBridge = () =>
  typeof window !== 'undefined' &&
  window.flutter_inappwebview &&
  typeof window.flutter_inappwebview.callHandler === 'function'

const callAppHandler = async (handlerName, ...args) => {
  if (!hasAppBridge()) return false
  try {
    await window.flutter_inappwebview.callHandler(handlerName, ...args)
    return true
  } catch (error) {
    console.warn('[umami] App 交互上报失败:', error)
    return false
  }
}

// iOS 设备 machine -> 机型名称映射表
const IOS_MACHINE_MODEL_MAP = {
  'iPhone1,1': 'iPhone',
  'iPhone1,2': 'iPhone 3G',
  'iPhone2,1': 'iPhone 3GS',
  'iPhone3,1': 'iPhone 4',
  'iPhone3,2': 'iPhone 4',
  'iPhone3,3': 'iPhone 4 (CDMA)',
  'iPhone4,1': 'iPhone 4S',
  'iPhone5,1': 'iPhone 5',
  'iPhone5,2': 'iPhone 5',
  'iPhone5,3': 'iPhone 5c',
  'iPhone5,4': 'iPhone 5c',
  'iPhone6,1': 'iPhone 5s',
  'iPhone6,2': 'iPhone 5s',
  'iPhone7,2': 'iPhone 6',
  'iPhone7,1': 'iPhone 6 Plus',
  'iPhone8,1': 'iPhone 6s',
  'iPhone8,2': 'iPhone 6s Plus',
  'iPhone8,4': 'iPhone SE (第 1 代)',
  'iPhone9,1': 'iPhone 7',
  'iPhone9,3': 'iPhone 7',
  'iPhone9,2': 'iPhone 7 Plus',
  'iPhone9,4': 'iPhone 7 Plus',
  'iPhone10,1': 'iPhone 8',
  'iPhone10,4': 'iPhone 8',
  'iPhone10,2': 'iPhone 8 Plus',
  'iPhone10,5': 'iPhone 8 Plus',
  'iPhone10,3': 'iPhone X',
  'iPhone10,6': 'iPhone X',
  'iPhone11,2': 'iPhone XS',
  'iPhone11,4': 'iPhone XS Max',
  'iPhone11,6': 'iPhone XS Max',
  'iPhone11,8': 'iPhone XR',
  'iPhone12,1': 'iPhone 11',
  'iPhone12,3': 'iPhone 11 Pro',
  'iPhone12,5': 'iPhone 11 Pro Max',
  'iPhone12,8': 'iPhone SE (第 2 代)',
  'iPhone13,1': 'iPhone 12 mini',
  'iPhone13,2': 'iPhone 12',
  'iPhone13,3': 'iPhone 12 Pro',
  'iPhone13,4': 'iPhone 12 Pro Max',
  'iPhone14,4': 'iPhone 13 mini',
  'iPhone14,5': 'iPhone 13',
  'iPhone14,2': 'iPhone 13 Pro',
  'iPhone14,3': 'iPhone 13 Pro Max',
  'iPhone14,6': 'iPhone SE (第 3 代)',
  'iPhone14,7': 'iPhone 14',
  'iPhone14,8': 'iPhone 14 Plus',
  'iPhone15,2': 'iPhone 14 Pro',
  'iPhone15,3': 'iPhone 14 Pro Max',
  'iPhone15,4': 'iPhone 15',
  'iPhone15,5': 'iPhone 15 Plus',
  'iPhone16,1': 'iPhone 15 Pro Max',
  'iPhone16,2': 'iPhone 15 Pro',
  'iPhone17,1': 'iPhone 16 Pro Max',
  'iPhone17,2': 'iPhone 16 Pro',
  'iPhone17,3': 'iPhone 16',
  'iPhone17,4': 'iPhone 16 Plus',
  'iPhone18,1': 'iPhone 17 Pro Max',
  'iPhone18,2': 'iPhone 17 Pro',
  'iPhone18,3': 'iPhone 17',
  'iPhone18,4': 'iPhone 17 Plus'
}

// 从字符串中提取 utsname.machine（兼容 JSON 与非 JSON 文本）
const extractMachineFromText = (text) => {
  const normalized = sanitizeString(text)
  if (!normalized) return ''
  const jsonMatch = normalized.match(/"machine"\s*:\s*"([^"]+)"/)
  if (jsonMatch) return sanitizeString(jsonMatch[1])
  const looseMatch = normalized.match(/machine\s*:\s*([A-Za-z0-9,]+)/)
  return looseMatch ? sanitizeString(looseMatch[1]) : ''
}

// 从多层结构中获取 iOS machine 值
const getIOSMachine = (info = {}) => {
  if (!isPlainObject(info)) return ''
  if (isPlainObject(info.utsname) && info.utsname.machine) {
    return sanitizeString(info.utsname.machine)
  }
  if (isPlainObject(info.ex) && isPlainObject(info.ex.utsname) && info.ex.utsname.machine) {
    return sanitizeString(info.ex.utsname.machine)
  }
  if (typeof info.ex === 'string') {
    return extractMachineFromText(info.ex)
  }
  return ''
}

// 规范化 machine（去掉末尾冗余逗号）
const normalizeIOSMachine = (machine) => sanitizeString(machine).replace(/,+$/, '')

// 根据 machine 转换为机型名称，未命中则回退为 machine 本身
const mapIOSMachineToModel = (machine) => {
  const normalized = normalizeIOSMachine(machine)
  return IOS_MACHINE_MODEL_MAP[normalized] || normalized
}

// 统一整理设备信息（平台/品牌/机型/版本）
const normalizeDeviceInfo = (info = {}) => {
  if (!isPlainObject(info)) return {}
  const platform = sanitizeString(
    info.platform || info.os || info.system || info.deviceSystem || info.deviceOS
  )
  const device_brand = sanitizeString(info.brand || info.device_brand)
  const device_model_raw = sanitizeString(info.model || info.device_model || '')
  const isIOS = platform.toLowerCase() === 'ios' || device_brand.toLowerCase() === 'apple'
  const machine = isIOS ? getIOSMachine(info) : ''
  const device_model = machine ? mapIOSMachineToModel(machine) : device_model_raw
  const app_version = sanitizeString(info.appVersion || info.app_version)
  return { platform, device_brand, device_model, app_version }
}

// 统一整理用户信息字段
const normalizeUserInfo = (info = {}) => {
  if (!isPlainObject(info)) return {}
  return {
    userId: sanitizeString(info.userId || info.id || info.uid),
    userName: sanitizeString(info.userName || info.username || info.nickName || info.nickname),
    lastLoginIpLocation: sanitizeString(info.lastLoginIpLocation),
  }
}

let umamiScriptPromise = null

// 懒加载 Umami 脚本，避免首屏阻塞
const loadUmamiScript = () => {
  if (typeof window === 'undefined' || !UMAMI_SCRIPT_URL) {
    return Promise.resolve(false)
  }
  if (window.umami && typeof window.umami.track === 'function') {
    return Promise.resolve(true)
  }
  if (umamiScriptPromise) {
    return umamiScriptPromise
  }
  umamiScriptPromise = new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = UMAMI_SCRIPT_URL
    script.async = true
    // 显式指定网站 ID，确保 Umami 可识别站点
    script.setAttribute('data-website-id', pickWebsiteId())
    // 页面访问由 Vue Router 统一上报，禁用 SDK 首次加载时的自动 pageview
    script.setAttribute('data-auto-pageview', 'false')
    script.onload = () => resolve(true)
    script.onerror = () => {
      console.warn('[umami] 脚本加载失败:', UMAMI_SCRIPT_URL)
      resolve(false)
    }
    document.head.appendChild(script)
  })
  return umamiScriptPromise
}

// 获取 Umami 客户端，确保脚本已加载
// getUmamiClient 授权页面不加载可读取敏感输入的第三方脚本，App 埋点出口保持原行为。
const getUmamiClient = async () => {
  if (typeof window !== 'undefined' && /^\/(futu|longbridge|usmart)-auth\/?$/.test(window.location.pathname)) return null
  if (typeof window === 'undefined') return null
  const ready = await loadUmamiScript()
  if (!ready) return null
  const client = window.umami
  if (client && typeof client.track === 'function') {
    return client
  }
  return null
}

let cachedContext = null
let lastFetched = 0

// 组装基础埋点上下文（站点/设备/用户等）
const buildBaseContext = async () => {
  const website = pickWebsiteId()
  // const [bridgeDeviceInfoRaw, userInfoRaw] = await Promise.all([
  //   JSBridge.getDeviceInfo(),
  //   typeof JSBridge.getUserInfo === 'function' ? JSBridge.getUserInfo() : null
  // ])
  // const userInfoRaw = typeof JSBridge.getUserInfo === 'function' ? JSBridge.getUserInfo() : null
  // const bridgeDeviceInfoRaw = getDeviceInfo() || {}

  // 语言通过 Cookie 获取，优先使用 App 写入值
  const language = getCookieLanguage() || 'US'
  const uaPlatform = parsePlatformFromUA()
  // 用户信息由 Cookie 读取（由 App 写入）
  const userInfoRaw = getCookie(UserInfoKey) || '{}'
  // 设备平台通过 JSBridge 获取，品牌型号通过 Cookie 获取
  // const bridgeDeviceInfo = normalizeDeviceInfo(bridgeDeviceInfoRaw)
  const cookieDeviceInfo = normalizeDeviceInfo(getCookieDeviceInfo())
  const userInfo = normalizeUserInfo(userInfoRaw)

  const platform = cookieDeviceInfo.platform || uaPlatform
  const device_brand = cookieDeviceInfo.device_brand || ''
  const device_model = cookieDeviceInfo.device_model || ''
  const app_version = cookieDeviceInfo.app_version || ''
  const hostname = pickHostname()
  // console.log('hostname', hostname);
  
  const referrer =
    typeof document !== 'undefined' ? sanitizeString(document.referrer) : ''

  // const country = userInfo.country || ''
  // const state = userInfo.state || ''
  // const city = userInfo.city || ''
  // 用户最近登录 IP 所在地
  const lastLoginIpLocation = userInfo.lastLoginIpLocation || ''

  return {
    website,
    hostname,
    language,
    referrer,
    url: '/',
    platform,
    device_brand,
    device_model,
    app_version,
    lastLoginIpLocation
  }
}

// 带缓存的上下文读取，减少重复计算
const getBaseContext = async () => {
  const now = Date.now()
  if (cachedContext && now - lastFetched < CACHE_TTL_MS) {
    return cachedContext
  }
  cachedContext = await buildBaseContext()
  lastFetched = now
  return cachedContext
}

// 规范化 URL，确保以 / 开头
const normalizeUrl = (url) => {
  const normalized = sanitizeString(url)
  if (!normalized) return '/'
  return normalized.startsWith('/') ? normalized : `/${normalized}`
}

// 规范化事件名（空格转下划线）
const normalizeEventName = (event) => {
  const normalized = sanitizeString(event).replace(/\s+/g, '_')
  return normalized || 'event'
}

// 清空上下文缓存
export const resetUmamiContext = () => {
  cachedContext = null
  lastFetched = 0
}

// 页面访问埋点
export const trackPageView = async ({ url, referrer } = {}) => {
  try {
    const base = await getBaseContext()
    const refFromArg = sanitizeString(referrer)
    const payload = {
      ...base,
      url: normalizeUrl(url || (typeof window !== 'undefined' ? window.location.pathname : '')),
      referrer: base.referrer || refFromArg
    }
    if (!payload.website) return
    const sentByApp = await callAppHandler('umamiTrackPageView', payload)
    if (sentByApp) return
    const client = await getUmamiClient()
    if (!client) return
    await client.track(payload)
  } catch (error) {
    console.warn('[umami] 页面埋点失败:', error)
  }
}

// 行为事件埋点
export const trackBehaviorEvent = async (event, { url, ext } = {}) => {
  if (isBrokerWebAuthorization()) return
  try {
    const base = await getBaseContext()
    const { referrer: _ignored, ...data } = base
    data.url = normalizeUrl(url || data.url || (typeof window !== 'undefined' ? window.location.pathname : ''))
    data.ext = isPlainObject(ext) ? ext : {}
    // 事件名规范：url:event，避免跨页面事件合并
    const eventName = `${data.url}:${normalizeEventName(event)}`
    if (!data.website) return
    const sentByApp = await callAppHandler('umamiTrackEvent', {
      name: eventName,
      data
    })
    if (sentByApp) return
    const client = await getUmamiClient()
    if (!client) return
    await client.track(eventName, data)
  } catch (error) {
    console.warn('[umami] 事件埋点失败:', error)
  }
}

// 用户身份验证上报（Umami identify）
export const identifyUser = async (uniqueId, data = {}) => {
  const client = await getUmamiClient()
  const normalizedId = sanitizeString(uniqueId)
  if (!client || !normalizedId) return
  try {
    const base = await getBaseContext()
    const lastLoginIpLocation = sanitizeString(
      data.lastLoginIpLocation || base.lastLoginIpLocation
    )
    const payload = {
      nickname: sanitizeString(data.nickname),
      language: base.language,
      platform: base.platform,
      device_brand: base.device_brand,
      device_model: base.device_model,
      app_version: base.app_version,
      lastLoginIpLocation
    }
    console.log('[umami] 身份验证上报:', normalizedId, payload)
    if (typeof client.identify === 'function') {
      await client.identify(normalizedId, payload)
    }
  } catch (error) {
    console.warn('[umami] 身份验证上报失败:', error)
  }
}

export default {
  trackPageView,
  trackBehaviorEvent,
  identifyUser,
  resetUmamiContext
}
